// @ts-check

/**
 * @file Builds the per-word Task picker search descriptor.
 *
 * Every word in the search must match the task name, the project name, the task
 * id or the project id. Words are combined with AND, the four alternatives of
 * one word with OR.
 *
 * Project-name matches are resolved to project ids up front because a second
 * Ransack condition on the same `project` relationship adds a duplicate join and
 * fails with `ER_NONUNIQ_TABLE`. `projectIdIn` reads the plain `project_id`
 * column, so the whole descriptor joins nothing.
 */

import {Project} from "../../frontend-models/project.js"

/** Maximum number of words one search may combine. */
export const TASK_SEARCH_MAX_TERMS = 8

/** @typedef {{resolveProjectIds?: (term: string) => Promise<string[]>}} TaskSearchArgs */

/**
 * Splits a picker search into the distinct words that must each match.
 * @param {string | null | undefined} searchValue Raw search text.
 * @returns {string[]} Trimmed, de-duplicated, bounded words.
 */
export function taskSearchTerms(searchValue) {
  if (typeof searchValue !== "string") return []

  const words = searchValue.trim().split(/\s+/u).filter((word) => word.length > 0)

  return [...new Set(words)].slice(0, TASK_SEARCH_MAX_TERMS)
}

/**
 * Builds the Ransack descriptor for already-tokenized words.
 * @param {{projectIdsByTerm?: Record<string, string[]>, terms: string[]}} args Words and their project-id matches.
 * @returns {Record<string, unknown>} Ransack params containing only the search groups.
 */
export function taskSearchRansackParams({projectIdsByTerm = {}, terms}) {
  if (terms.length === 0) return {}

  const groups = terms.map((term) => {
    /** @type {Record<string, unknown>} */
    const group = {m: "or", idEq: term, nameCont: term, projectIdEq: term}
    const projectIds = projectIdsByTerm[term] || []

    if (projectIds.length > 0) group.projectIdIn = projectIds

    return group
  })

  return {g: groups}
}

/**
 * Resolves and caches the project ids whose name contains one search word.
 *
 * The lookup is authorized by the Project resource, so a user only ever gets
 * ids of projects they can read.
 */
export default class TaskPickerSearch {
  /**
   * Creates the search helper.
   * @param {TaskSearchArgs} [args] Injectable project resolver.
   */
  constructor({resolveProjectIds = resolveProjectIdsByName} = {}) {
    this.resolveProjectIds = resolveProjectIds
    /** @type {Map<string, string[]>} */
    this.projectIdsByTerm = new Map()
  }

  /**
   * Builds the search descriptor for one picker query.
   * @param {{searchValue?: string | null}} args Picker search text.
   * @returns {Promise<Record<string, unknown>>} Ransack params for the search words.
   * @rejects {Error} When a project-name lookup fails.
   */
  async paramsFor({searchValue}) {
    const terms = taskSearchTerms(searchValue)
    /** @type {Record<string, string[]>} */
    const projectIdsByTerm = {}

    for (const term of terms) {
      projectIdsByTerm[term] = await this.projectIdsForTerm(term)
    }

    return taskSearchRansackParams({projectIdsByTerm, terms})
  }

  /**
   * Returns the cached project ids for one word, resolving them once.
   * @param {string} term Search word.
   * @returns {Promise<string[]>} Matching project ids.
   * @rejects {Error} When the project lookup fails.
   */
  async projectIdsForTerm(term) {
    const cached = this.projectIdsByTerm.get(term)

    if (cached) return cached

    const projectIds = await this.resolveProjectIds(term)

    if (this.projectIdsByTerm.size >= 100) this.projectIdsByTerm.clear()
    this.projectIdsByTerm.set(term, projectIds)

    return projectIds
  }
}

/**
 * Resolves project ids by project name through the Project resource.
 * @param {string} term Search word.
 * @returns {Promise<string[]>} Ids of readable projects whose name contains the word.
 */
async function resolveProjectIdsByName(term) {
  const projects = await Project
    .ransack({nameCont: term})
    .select(["id"])
    .toArray()

  return projects.map((project) => String(project.id()))
}
